/**
 * Chat links into the app (J5). A path is an app link only when it matches a
 * route in the generated catalog (@mono/app-routes) — same page, same allowed
 * query keys — and chat renders it as a same-tab router link only when a tool
 * returned that exact href to this user. Everything else that looks like a
 * path into the app is plain text: the model can never invent a link.
 */ import { BUILDERS, ROUTES } from '@mono/app-routes';
const ORIGIN = 'https://app.invalid';
const ZERO_WIDTH = /[\u200b-\u200d\u2060\ufeff]/g;
function splitTemplate(path) {
    const q = path.indexOf('?');
    return q < 0 ? {
        pathname: path,
        query: new URLSearchParams()
    } : {
        pathname: path.slice(0, q),
        query: new URLSearchParams(path.slice(q + 1))
    };
}
const isParam = (s)=>s.startsWith(':') && s.length > 1;
const COMPILED = ROUTES.map((route)=>{
    const { pathname, query } = splitTemplate(route.path);
    const allowed = new Set(query.keys());
    const builder = route.builder ? BUILDERS.find((b)=>b.name === route.builder) : undefined;
    for (const key of builder?.query ?? [])allowed.add(key);
    const fixed = [
        ...query.entries()
    ].filter(([, v])=>!isParam(v));
    return {
        route,
        segments: pathname.split('/').filter(Boolean),
        fixed,
        allowed
    };
});
/**
 * Parse a same-origin relative href. Anything with a scheme, a host, a
 * protocol-relative prefix, backslashes or a fragment is not an app path.
 */ function parseRelative(href) {
    // A model sometimes slips a zero-width character in front of the path
    // (seen live: "(\u200c/delivery/…)"); it is invisible, so it is not part of
    // the link. Anything else stays as written.
    const raw = href.replace(ZERO_WIDTH, '').trim();
    if (!raw.startsWith('/') || raw.startsWith('//') || raw.includes('\\') || raw.includes('#')) {
        return null;
    }
    try {
        const url = new URL(raw, ORIGIN);
        return url.origin === ORIGIN ? url : null;
    } catch  {
        return null;
    }
}
/** The catalog route a relative href opens, or null when it is not one. */ export function matchAppRoute(href) {
    const url = parseRelative(href);
    if (!url) return null;
    const segments = url.pathname.split('/').filter(Boolean);
    const keys = [
        ...url.searchParams.keys()
    ];
    for (const c of COMPILED){
        if (c.segments.length !== segments.length) continue;
        const pathOk = c.segments.every((seg, i)=>isParam(seg) ? segments[i] !== '' : seg === segments[i]);
        if (!pathOk) continue;
        if (!keys.every((k)=>c.allowed.has(k))) continue;
        if (!c.fixed.every(([k, v])=>url.searchParams.get(k) === v)) continue;
        if (keys.some((k)=>url.searchParams.get(k) === '')) continue;
        return c.route;
    }
    return null;
}
/**
 * One spelling per destination: pathname plus query sorted by key, so a
 * model that reorders a tool's query still matches it and nothing else.
 */ export function canonicalAppHref(href) {
    const url = parseRelative(href);
    if (!url) return null;
    const params = [
        ...url.searchParams.entries()
    ].sort(([a], [b])=>a < b ? -1 : a > b ? 1 : 0);
    const query = new URLSearchParams(params).toString();
    return query ? `${url.pathname}?${query}` : url.pathname;
}
/** True for a link that leaves the app: http(s) and mailto only. */ export function isExternalHref(href) {
    try {
        const url = new URL(href.trim());
        return url.protocol === 'https:' || url.protocol === 'http:' || url.protocol === 'mailto:';
    } catch  {
        return false;
    }
}
/** Keys whose string values are links a tool returned (href, programHref, …). */ const HREF_KEY = /(^href$|Href$|_href$)/;
const toolHrefCache = new WeakMap();
/**
 * Every app href in one tool output, canonicalised. Walks the JSON the tool
 * returned (the same JSON the model read); cached per output object, so a
 * streaming re-render does not walk finished results again.
 */ export function toolOutputHrefs(output) {
    if (output === null || typeof output !== 'object') return [];
    const cached = toolHrefCache.get(output);
    if (cached) return cached;
    const found = [];
    const stack = [
        output
    ];
    let visited = 0;
    while(stack.length > 0 && visited < 200000){
        const node = stack.pop();
        visited++;
        if (Array.isArray(node)) {
            for (const item of node)if (item !== null && typeof item === 'object') stack.push(item);
            continue;
        }
        if (node === null || typeof node !== 'object') continue;
        for (const [key, value] of Object.entries(node)){
            if (typeof value === 'string') {
                if (HREF_KEY.test(key) && matchAppRoute(value)) {
                    const canon = canonicalAppHref(value);
                    if (canon) found.push(canon);
                }
            } else if (value !== null && typeof value === 'object') {
                stack.push(value);
            }
        }
    }
    toolHrefCache.set(output, found);
    return found;
}
